import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import { DoraButton } from "dora-shared/frontend";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useSelector } from "../../../redux-store";
import {
  clearSplitCargos,
  loadCargosForSplit,
  selectSplitCargos,
  selectSplitCargosLoading,
  unsplitRoute,
} from "../../../ducks/data/split-cargo";
import { useTranslation } from "react-i18next";

export default function UnsplitRouteDialog({
  routeId,
  cargoOrder,
  onClose,
}: {
  routeId: string;
  cargoOrder: string[];
  onClose: () => void;
}) {
  const dispatch = useAppDispatch();
  const allLoadedCargos = useSelector(selectSplitCargos);
  const loading = useSelector(selectSplitCargosLoading);
  const { t } = useTranslation(["split", "components"]);

  // Keyed by the ids, not the array: every route view update replaces the
  // route's cargoOrder, and reloading (and resetting the selection) on each
  // one dispatched from an effect per socket message (DORA-385).
  const cargoOrderKey = cargoOrder.join(",");

  useEffect(() => {
    dispatch(clearSplitCargos());
    dispatch(loadCargosForSplit(cargoOrder));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargoOrderKey, dispatch]);

  const splitCargos = useMemo(
    () => allLoadedCargos.filter((c) => c.hasBeenSplit),
    [allLoadedCargos]
  );

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setSelectedIds(splitCargos.map((c) => c.id));
  }, [splitCargos]);

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const onUnsplitClick = async () => {
    if (!selectedIds.length) return;
    setIsSubmitting(true);
    try {
      await dispatch(
        unsplitRoute({ routeId, cargosIds: selectedIds })
      ).unwrap();
      onClose();
    } catch {
      // Already reported (and toasted) by the thunk.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} onClick={(e) => e.stopPropagation()}>
      <DialogTitle>{t("split:unsplitRoute")}</DialogTitle>
      <DialogContent>
        <div style={{ minWidth: 300 }}>
          {isSubmitting ? (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <CircularProgress size={32} />
              <span>{t("split:processingUnsplit")}</span>
            </div>
          ) : loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <CircularProgress size={32} />
              <span>{t("split:loadingOrders")}</span>
            </div>
          ) : splitCargos.length === 0 ? (
            <div>{t("split:noSplitOrdersFound")}</div>
          ) : (
            <div>
              <div className="mb-2">
                <label>{t("split:selectOrdersToUnsplit")}</label>
              </div>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>{t("split:tableColumns.ref")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {splitCargos.map((cargo) => (
                    <TableRow key={cargo.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.includes(cargo.id)}
                          onChange={() => toggle(cargo.id)}
                        />
                      </TableCell>
                      <TableCell>{cargo.ref}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <div className="flex justify-end gap-2 items-center">
          <DoraButton variant="primary" onClick={onClose}>
            {t("components:buttonLabels.cancel")}
          </DoraButton>
          <DoraButton
            variant="primary-filled"
            onClick={onUnsplitClick}
            disabled={!selectedIds.length || isSubmitting}
          >
            {isSubmitting ? t("split:processing") : t("split:unsplitConfirmButton")}
          </DoraButton>
        </div>
      </DialogActions>
    </Dialog>
  );
}
